import _ from "lodash";
import React from "react";
import { DeleteOutlined, PencilLineOutlined } from "@toddle-edu/ds-icons";
import { getCategorySets } from "routes/routes/Administrator/routes/DPAcademicSetup/routes/AssessmentCategories/modules/AssessmentCategoriesModule";
import {
  CATEGORY_SCORE,
  CORE_AND_OTHER_TYPES,
  CORE_TOK_AND_OTHER_TYPES,
} from "Constants/stringConstants";
import update from "immutability-helper";
import { Trans } from "react-i18next";

const components = { b: <b /> };

const getCategoryLabel = ({ label, defaultLabel, categoryScoreLabel }) =>
  !_.isNil(categoryScoreLabel) ? label : defaultLabel;

const _getHeaderItems = ({
  t,
  isSameScale,
  subjectLevelGenericTags,
  hasSLWeight,
  hasHLWeight,
  criteriaLabels,
}) => {
  const categoryScoreLabel = _.get(criteriaLabels, CATEGORY_SCORE, null);
  const tagLabels = [t("common:SL"), t("common:HL")];
  const headerItems = [t("academicSetup:grade_boundary")];
  if (isSameScale) {
    headerItems.push(
      getCategoryLabel({
        label: `${categoryScoreLabel} (${t("common:join_array_with_and", {
          array: tagLabels,
        })})`,
        defaultLabel: t("academicSetup:score_range_for_sl_and_hl"),
        categoryScoreLabel,
      })
    );
  } else {
    if (_.isEmpty(subjectLevelGenericTags)) {
      headerItems.push(t("assessmentEvaluation:score_range"));
    } else {
      if (hasSLWeight) {
        headerItems.push(
          getCategoryLabel({
            label: `${categoryScoreLabel} (${t("common:SL")})`,
            defaultLabel: t("academicSetup:score_range_for_sl"),
            categoryScoreLabel,
          })
        );
      }
      if (hasHLWeight) {
        headerItems.push(
          getCategoryLabel({
            label: `${categoryScoreLabel} (${t("common:HL")})`,
            defaultLabel: t("academicSetup:score_range_for_hl"),
            categoryScoreLabel,
          })
        );
      }
    }
  }
  return headerItems;
};

export const getHeaderItems = _.memoize(_getHeaderItems, params =>
  JSON.stringify(params)
);

export const getOptionsWithIcon = t => {
  const optionsWithIcon = [
    {
      key: "edit",
      label: t("common:edit"),
      icon: <PencilLineOutlined size="xx-small" />,
    },
    {
      key: "delete",
      isDestructive: true,
      label: t("common:delete"),
      icon: <DeleteOutlined size="xx-small" />,
    },
  ];

  return optionsWithIcon;
};

const _getCategoryGradeBoundaryValues = ({
  categoryGradeBoundaries,
  categoryGradeBoundaryValues,
  subjectType,
}) => {
  const isTOKSubject = _.isEqual(subjectType, "TOK");
  const categories = _.get(categoryGradeBoundaries, "categories", []);
  const gradeBoundaryCategories = _.orderBy(
    categories,
    "label",
    isTOKSubject ? "desc" : "asc"
  );
  const updatedValues = _.cloneDeep(categoryGradeBoundaryValues);

  _.forEach(gradeBoundaryCategories, (gradeBoundaryCategory, index) => {
    const gradeBoundaryCategoryId = _.get(gradeBoundaryCategory, "id");
    const categoryValues =
      _.get(gradeBoundaryCategory, "values") ||
      _.get(gradeBoundaryCategory, "scoreValues", []);
    _.set(_.get(updatedValues, [index]), "id", gradeBoundaryCategoryId);
    _.forEach(categoryValues, categoryValue => {
      const categoryValueId = _.get(categoryValue, "id");
      const minValue = _.toString(_.get(categoryValue, "minValue"));
      const maxValue = _.toString(_.get(categoryValue, "maxValue"));
      const genericTagLabel = _.get(categoryValue, "genericTag.label", "SL");
      _.set(_.get(updatedValues, [index]), [genericTagLabel], {
        minValue,
        maxValue,
        id: categoryValueId,
      });
    });
  });

  return updatedValues;
};

export const getCategoryGradeBoundaryValues = _.memoize(
  _getCategoryGradeBoundaryValues,
  params => JSON.stringify(params)
);

export const getDropdownOptions = ({
  t,
  subjectType,
  doesAllGradesHaveCategorySet = false,
  doesAllSubjectsHaveCategorySet = false,
  isClassSettingsContext = false,
  isNewTemplateDuplicationFlowEnabled = false,
  doesAllTermsHaveCategorySet = false,
  isGradingPeriodUnlocked = true,
}) => {
  const duplicateOption = _.includes(
    ["TOK", "OTHERS", "LD", "PPS"],
    subjectType
  )
    ? []
    : isNewTemplateDuplicationFlowEnabled
    ? [
        {
          key: "duplicate",
          label: t("academicSetup:duplicate_to_other_subjects"),
          disabledTooltip: t(
            "academicSetup:template_already_exists_for_all_subjects"
          ),
          disabled: doesAllSubjectsHaveCategorySet,
          visible: !isClassSettingsContext,
        },
      ]
    : [
        {
          key: "duplicate_to_all",
          label: t("academicSetup:duplicate_to_all_subjects_of_term"),
          visible: !isClassSettingsContext,
          disabled: !isGradingPeriodUnlocked,
          disabledTooltip: !isGradingPeriodUnlocked
            ? t(
                "academicSetup:grading_period_is_locked_grading_configurations_cannot_be_edited"
              )
            : null,
        },
      ];

  const dropdownOptions = [
    {
      key: "edit",
      label: t("common:edit"),
      visible: true,
      disabled: !isGradingPeriodUnlocked,
      disabledTooltip: !isGradingPeriodUnlocked
        ? t(
            "academicSetup:grading_period_is_locked_grading_configurations_cannot_be_edited"
          )
        : null,
    },
    {
      key: "duplicate_to_other_grades",
      label: t("academicSetup:duplicate_to_other_grades_of_this_subject"),
      disabledTooltip: t(
        "academicSetup:template_already_exists_for_all_grades"
      ),
      disabled: doesAllGradesHaveCategorySet,
      visible: !isClassSettingsContext,
    },
    {
      key: "duplicate_to_other_terms",
      label: t("academicSetup:duplicate_to_other_terms"),
      disabledTooltip: t(
        "academicSetup:category_sets_already_created_for_all_terms"
      ),
      disabled: doesAllTermsHaveCategorySet,
      visible: isClassSettingsContext,
    },
    ...duplicateOption,
    {
      key: "delete",
      isDestructive: true,
      label: t("common:delete"),
      visible: !isClassSettingsContext,
      disabled: !isGradingPeriodUnlocked,
      disabledTooltip: !isGradingPeriodUnlocked
        ? t(
            "academicSetup:grading_period_is_locked_grading_configurations_cannot_be_edited"
          )
        : null,
    },
  ];

  return _.filter(dropdownOptions, "visible");
};

const _checkAreScoresValid = ({
  t,
  categoryGradeBoundaryValues,
  hasSL,
  hasHL,
  noSLorHL,
  isSameScale,
}) => {
  let errors = {};
  for (let i = 0; i < _.size(categoryGradeBoundaryValues); i++) {
    const {
      SL: currentSL,
      HL: currentHL,
      grade: id,
    } = categoryGradeBoundaryValues[i];
    const { SL: nextSL = {}, HL: nextHL = {}, grade: nextId } =
      categoryGradeBoundaryValues[i + 1] || {};

    const isLastRow = i == _.size(categoryGradeBoundaryValues) - 1;
    const isFirstRow = i == 0;

    // SL, core, same scale Errors

    if (hasSL || noSLorHL || isSameScale) {
      const {
        minValue: _currentMinScoreSL,
        maxValue: _currentMaxScoreSL,
      } = currentSL;

      const currentMinScoreSL = parseFloat(_currentMinScoreSL);
      const currentMaxScoreSL = parseFloat(_currentMaxScoreSL);

      if (categoryGradeBoundaryValues[i + 1]) {
        const { minValue: _nextMinScoreSL } = nextSL;
        const nextMinScoreSL = parseFloat(_nextMinScoreSL);

        if (currentMinScoreSL >= nextMinScoreSL) {
          errors = {
            ...errors,
            [`${isFirstRow ? nextId : id}_SL_minScore`]: {
              errorMsg: t(
                `academicSetup:${
                  isFirstRow
                    ? "min_score_must_be_higher_than_label"
                    : "min_score_lower_than_max_score_label"
                }`,
                {
                  label: isFirstRow ? currentMinScoreSL : nextMinScoreSL,
                }
              ),
            },
          };
        }
      }

      if (_.isEmpty(_.toString(_currentMinScoreSL))) {
        errors = {
          ...errors,
          [`${id}_SL_minScore`]: {
            errorMsg: t("academicSetup:this_field_is_compulsory"),
          },
        };
      }

      if (isLastRow && _.size(categoryGradeBoundaryValues) > 1) {
        // check for last row between minscore and maxscore
        if (currentMinScoreSL > currentMaxScoreSL) {
          errors = {
            ...errors,
            [`${id}_SL_minScore`]: {
              errorMsg: t(
                "academicSetup:min_score_higher_than_max_score_label",
                {
                  label: currentMaxScoreSL,
                }
              ),
            },
          };
        }
      }
    }

    if (hasHL) {
      const {
        minValue: _currentMinScoreHL,
        maxValue: _currentMaxScoreHL,
      } = currentHL;

      const currentMinScoreHL = parseFloat(_currentMinScoreHL);
      const currentMaxScoreHL = parseFloat(_currentMaxScoreHL);

      if (categoryGradeBoundaryValues[i + 1]) {
        const { minValue: _nextMinScoreHL } = nextHL;
        const nextMinScoreHL = parseFloat(_nextMinScoreHL);

        if (currentMinScoreHL >= nextMinScoreHL) {
          errors = {
            ...errors,
            [`${isFirstRow ? nextId : id}_HL_minScore`]: {
              errorMsg: t(
                `academicSetup:${
                  isFirstRow
                    ? "min_score_must_be_higher_than_label"
                    : "min_score_lower_than_max_score_label"
                }`,
                {
                  label: isFirstRow ? currentMinScoreHL : nextMinScoreHL,
                }
              ),
            },
          };
        }
      }

      if (_.isEmpty(_.toString(_currentMinScoreHL))) {
        errors = {
          ...errors,
          [`${id}_HL_minScore`]: {
            errorMsg: t("academicSetup:this_field_is_compulsory"),
          },
        };
      }

      if (isLastRow && _.size(categoryGradeBoundaryValues) > 1) {
        // check for last row between minscore and maxscore
        if (currentMinScoreHL > currentMaxScoreHL) {
          errors = {
            ...errors,
            [`${id}_HL_minScore`]: {
              errorMsg: t(
                "academicSetup:min_score_higher_than_max_score_label",
                {
                  label: currentMaxScoreHL,
                }
              ),
            },
          };
        }
      }
    }
  }

  // when isSameScale is true filter out HL errors
  // because in same scale we use "SL" as generic tag
  if (isSameScale) {
    errors = _.pick(
      errors,
      _.keys(errors)?.filter(error => _.includes(error, "SL"))
    );
  }
  return errors;
};

export const checkAreScoresValid = _.memoize(
  params => _checkAreScoresValid(params),
  params => JSON.stringify(params)
);

const _copySLGradeScaleToHL = ({ categoryGradeBoundaryValues }) => {
  const updatedCategoryGradeBoundaryValues = _.cloneDeep(
    categoryGradeBoundaryValues
  );
  _.forEach(categoryGradeBoundaryValues, (item, index) => {
    const scoreRangeSL = _.get(item, "SL", "");
    const minValue = _.get(scoreRangeSL, "minValue", "");
    const maxValue = _.get(scoreRangeSL, "maxValue", "");
    _.set(
      updatedCategoryGradeBoundaryValues,
      [index, "HL", "minValue"],
      minValue
    );
    _.set(
      updatedCategoryGradeBoundaryValues,
      [index, "HL", "maxValue"],
      maxValue
    );
  });
  return updatedCategoryGradeBoundaryValues;
};

export const copySLGradeScaleToHL = _.memoize(_copySLGradeScaleToHL, params =>
  JSON.stringify(params)
);

const _checkIsSameScale = ({
  categoryGradeBoundaryValues,
  subjectLevelGenericTags,
}) => {
  let isSameScale =
    !_.isEmpty(categoryGradeBoundaryValues) &&
    _.size(subjectLevelGenericTags) > 1;

  _.forEach(categoryGradeBoundaryValues, item => {
    const scoreRangeSL = _.get(item, "SL");
    const scoreRangeHL = _.get(item, "HL");
    if (
      !_.isEqual(
        _.get(scoreRangeSL, "minValue"),
        _.get(scoreRangeHL, "minValue")
      ) ||
      !_.isEqual(
        _.get(scoreRangeSL, "maxValue"),
        _.get(scoreRangeHL, "maxValue")
      )
    ) {
      isSameScale = false;
      return false;
    }
  });
  return isSameScale;
};

export const checkIsSameScale = _.memoize(_checkIsSameScale, params =>
  JSON.stringify(params)
);

export const SL_HL_GENERIC_TAGS = ["SL", "HL"];

const _getSubjectLevelGenericTagsFromFlatVariants = ({ flatVariants }) => {
  return _.orderBy(
    _.filter(
      _.uniqBy(
        _.map(flatVariants, item => _.get(item, "level")),
        "id"
      ),
      tag => _.includes(SL_HL_GENERIC_TAGS, _.get(tag, "label", ""))
    ),
    "label",
    "desc"
  );
};

export const getSubjectLevelGenericTagsFromFlatVariants = _.memoize(
  _getSubjectLevelGenericTagsFromFlatVariants,
  params => JSON.stringify(params)
);

export const getIsCategorySetExistForGrade = _.memoize(
  ({ categorySets, gradeId }) => {
    return !_.isEmpty(
      _.find(categorySets, set => {
        const associatedEntities = _.get(set, "node.associatedEntities", []);
        return _.find(associatedEntities, entity => {
          const { id, __typename } = entity;
          return _.isEqual(__typename, "Grade") && _.isEqual(id, gradeId);
        });
      })
    );
  },
  params => JSON.stringify(params)
);

export const getCategorySetsMappingWithGrades = _.memoize(
  ({ categorySets, grades }) => {
    const categorySetsMappingWithGrades = {};
    _.forEach(grades, ({ id: gradeId }) => {
      categorySetsMappingWithGrades[gradeId] = getIsCategorySetExistForGrade({
        categorySets,
        gradeId,
      });
    });
    return categorySetsMappingWithGrades;
  },
  params => JSON.stringify(params)
);

export const getIsCategorySetExistForSubjectAndGrade = _.memoize(
  ({ subject, termId, gradeId }) => {
    const pointAssessmentSets = _.get(subject, "pointAssessmentSets.edges", []);
    const categorySets = getCategorySets({
      pointAssessmentSets,
      termId,
    });
    return getIsCategorySetExistForGrade({
      categorySets,
      gradeId,
    });
  },
  params => JSON.stringify(params)
);

export const getCategorySetMappedWithSubjectAndAllGradesData = _.memoize(
  ({ subject, termId, grades }) => {
    if (!termId) return {};

    const pointAssessmentSets = _.get(subject, "pointAssessmentSets.edges", []);
    const categorySets = getCategorySets({
      pointAssessmentSets,
      termId,
    });

    const categorySetsMappingWithGrades = getCategorySetsMappingWithGrades({
      categorySets,
      grades,
    });

    const isEveryGradeHasCategorySet = _.every(
      _.values(categorySetsMappingWithGrades),
      ele => ele === true
    );

    const isCategorySetsEmpty = _.every(
      _.values(categorySetsMappingWithGrades),
      ele => ele === false
    );

    return {
      categorySetsMappingWithGrades,
      isEveryGradeHasCategorySet,
      isCategorySetsEmpty,
    };
  },
  params => JSON.stringify(params)
);

export const getCategorySetsMappedWithAllSubjectsData = _.memoize(
  ({
    subjectGroups,
    rootNodes,
    grades,
    gradeId = null,
    termId,
    isCurriculumDP,
    subjectGroupId = null,
    isSource = false,
  }) => {
    let isCategorySetsExistsForAllSubjects = true;
    let categorySetsCount = 0;
    const _subjectGroups = isCurriculumDP
      ? subjectGroups
      : [{ subjects: rootNodes }];

    _.forEach(_subjectGroups, subjectGroup => {
      const { id, subjects } = subjectGroup;
      if (!_.isEmpty(subjectGroupId) && id !== subjectGroupId) return;
      _.forEach(subjects, subject => {
        if (!_.isEmpty(gradeId)) {
          if (
            !getIsCategorySetExistForSubjectAndGrade({
              subject,
              termId,
              gradeId,
            })
          ) {
            isCategorySetsExistsForAllSubjects = false;
          } else {
            categorySetsCount++;
          }
        } else {
          const {
            isEveryGradeHasCategorySet,
            isCategorySetsEmpty,
          } = getCategorySetMappedWithSubjectAndAllGradesData({
            subject,
            termId,
            grades,
          });

          if (
            (isSource && isCategorySetsEmpty) ||
            (!isSource && !isEveryGradeHasCategorySet)
          ) {
            isCategorySetsExistsForAllSubjects = false;
          } else {
            categorySetsCount++;
          }
        }
      });
    });

    return { categorySetsCount, isCategorySetsExistsForAllSubjects };
  },
  params => JSON.stringify(params)
);

export const getIsSubjectSelected = _.memoize(
  ({ associatedEntities, subjectId }) =>
    _.findIndex(
      associatedEntities,
      associatedEntity => _.get(associatedEntity, "subjectId") === subjectId
    ) > -1,
  params => JSON.stringify(params)
);

const getSelectableGrades = ({
  grades,
  subjectId,
  sourceGradeId,
  sourceSubjectId,
  isImportFromAnotherTerm,
  canOverrideExistingTemplates,
  termsCategorySetsMappingWithGradesData,
}) => {
  return _.map(
    _.filter(grades, grade => {
      const { id: gradeId } = grade;
      if (isImportFromAnotherTerm) {
        if (!canOverrideExistingTemplates) {
          return (
            _.get(
              termsCategorySetsMappingWithGradesData,
              `0.categorySetsMappingWithGrades.${gradeId}`
            ) &&
            !_.get(
              termsCategorySetsMappingWithGradesData,
              `1.categorySetsMappingWithGrades.${gradeId}`
            )
          );
        }
        return true;
      } else {
        if (!canOverrideExistingTemplates) {
          return !_.get(
            termsCategorySetsMappingWithGradesData,
            `1.categorySetsMappingWithGrades.${gradeId}`
          );
        }
        return !(gradeId === sourceGradeId && subjectId === sourceSubjectId);
      }
    }),
    "id"
  );
};

export const getUpdatedAssociatedEntities = _.memoize(
  ({
    associatedEntities,
    bulkSelectedGrades,
    subjectId,
    grades,
    gradeId,
    sourceSubjectId,
    termsCategorySetsMappingWithGradesData,
    isBulk = false,
    selectedSubjectsCount = 0,
    isImportFromAnotherTerm = false,
    canOverrideExistingTemplates = false,
  }) => {
    let updatedAssociatedEntities = _.cloneDeep(associatedEntities);
    const isSubjectSelected = getIsSubjectSelected({
      associatedEntities,
      subjectId,
    });
    if (isSubjectSelected || (isBulk && selectedSubjectsCount !== 0)) {
      updatedAssociatedEntities = _.filter(
        associatedEntities,
        associatedEntity => _.get(associatedEntity, "subjectId") !== subjectId
      );
    } else {
      const selectableGrades = getSelectableGrades({
        grades,
        subjectId,
        sourceSubjectId,
        sourceGradeId: gradeId,
        isImportFromAnotherTerm,
        canOverrideExistingTemplates,
        termsCategorySetsMappingWithGradesData,
      });
      updatedAssociatedEntities.push({
        subjectId,
        gradeIds: _.intersection(selectableGrades, bulkSelectedGrades), // only select available grades
      });
    }
    return updatedAssociatedEntities;
  },
  params => JSON.stringify(params)
);

export const getUpdatedAssociatedEntitiesForMultiSubjects = _.memoize(
  ({
    subjects,
    sourceTermId,
    targetTermId,
    grades,
    gradeId,
    sourceSubjectId,
    isImportFromAnotherTerm,
    associatedEntities,
    bulkSelectedGrades,
    selectedSubjectsCount,
    shouldApplyBulkGrades = false,
    canOverrideExistingTemplates = false,
  }) => {
    let updatedAssociatedEntities = _.cloneDeep(associatedEntities);
    _.forEach(subjects, subject => {
      const { id: subjectId } = subject;

      const termsCategorySetsMappingWithGradesData = _.map(
        [sourceTermId, targetTermId],
        termId =>
          getCategorySetMappedWithSubjectAndAllGradesData({
            subject,
            termId,
            grades,
          })
      );

      if (shouldApplyBulkGrades) {
        const selectableGrades = getSelectableGrades({
          grades,
          isImportFromAnotherTerm,
          canOverrideExistingTemplates,
          termsCategorySetsMappingWithGradesData,
        });
        const arrIndex = _.findIndex(
          updatedAssociatedEntities,
          associatedEntity => _.get(associatedEntity, "subjectId") === subjectId
        );
        updatedAssociatedEntities[arrIndex] = {
          ...updatedAssociatedEntities[arrIndex],
          gradeIds: _.intersection(selectableGrades, bulkSelectedGrades),
        };
      } else {
        const { isCheckboxDisabled } = getSubjectCheckboxData({
          subject,
          grades,
          sourceTermId,
          targetTermId,
          isImportFromAnotherTerm,
          canOverrideExistingTemplates,
        });

        if (
          (!isImportFromAnotherTerm && canOverrideExistingTemplates) ||
          !isCheckboxDisabled
        ) {
          updatedAssociatedEntities = getUpdatedAssociatedEntities({
            associatedEntities: updatedAssociatedEntities,
            bulkSelectedGrades,
            subjectId,
            grades,
            gradeId,
            sourceSubjectId,
            termsCategorySetsMappingWithGradesData,
            isBulk: true,
            selectedSubjectsCount,
            isImportFromAnotherTerm,
            canOverrideExistingTemplates,
          });
        }
      }
    });
    return updatedAssociatedEntities;
  },
  params => JSON.stringify(params)
);

export const getSubjectCheckboxData = _.memoize(
  ({
    subject,
    grades,
    sourceTermId,
    targetTermId,
    isImportFromAnotherTerm = false,
    sourceSubjectGenericTags,
    subjectLevelGenericTags,
    isCurriculumDP,
    canOverrideExistingTemplates,
  }) => {
    const termsCategorySetsMappingWithGradesData = _.map(
      [sourceTermId, targetTermId],
      termId =>
        getCategorySetMappedWithSubjectAndAllGradesData({
          subject,
          termId,
          grades,
        })
    );

    const selectableGrades = getSelectableGrades({
      grades,
      isImportFromAnotherTerm,
      canOverrideExistingTemplates,
      termsCategorySetsMappingWithGradesData,
    });

    let isCheckboxDisabled = false;

    const doBothSourceAndTargetSubjectsHaveSLorHL =
      !_.isEmpty(sourceSubjectGenericTags) &&
      !_.isEmpty(subjectLevelGenericTags);

    if (isCurriculumDP && doBothSourceAndTargetSubjectsHaveSLorHL) {
      isCheckboxDisabled = _.isEmpty(
        _.intersection(
          _.map(sourceSubjectGenericTags, "id"),
          _.map(subjectLevelGenericTags, "id")
        )
      );
    }

    isCheckboxDisabled = isCheckboxDisabled || _.isEmpty(selectableGrades);

    return { termsCategorySetsMappingWithGradesData, isCheckboxDisabled };
  },
  params => JSON.stringify(params)
);

export const getSelectableSubjectsCount = _.memoize(
  ({
    subjects,
    grades,
    sourceTermId,
    targetTermId,
    isImportFromAnotherTerm,
    canOverrideExistingTemplates,
  }) => {
    return _.get(
      _.filter(subjects, subject => {
        const { isCheckboxDisabled } = getSubjectCheckboxData({
          subject,
          grades,
          sourceTermId,
          targetTermId,
          isImportFromAnotherTerm,
          canOverrideExistingTemplates,
        });
        return !isCheckboxDisabled;
      }),
      "length",
      0
    );
  }
);

export const getIsCategorySetsExistsForAllSubjects = _.memoize(
  ({
    subjects,
    grades,
    sourceTermId,
    targetTermId,
    isImportFromAnotherTerm,
    canOverrideExistingTemplates,
  }) => {
    return _.every(
      _.map(subjects, subject => {
        const { isCheckboxDisabled } = getSubjectCheckboxData({
          subject,
          grades,
          sourceTermId,
          targetTermId,
          isImportFromAnotherTerm,
          canOverrideExistingTemplates,
        });
        return isCheckboxDisabled;
      }),
      ele => ele === true
    );
  },
  params => JSON.stringify(params)
);

export const getIsTemplatesImportButtonDisabled = _.memoize(
  ({ associatedEntities }) => {
    return (
      _.isEmpty(associatedEntities) ||
      !_.isEmpty(
        _.filter(associatedEntities, associatedEntity =>
          _.isEmpty(_.get(associatedEntity, "gradeIds", []))
        )
      )
    );
  },
  params => JSON.stringify(params)
);

export const getPluralLabelByParentType = ({ t, parentType }) => {
  switch (parentType) {
    case "ASSESSMENT_CATEGORIES":
      return t("academicSetup:category_sets");
    case "GRADE_BOUNDARIES":
    case "GRADE_SCALES":
      return t("academicSetup:grade_boundaries");
    default:
      return "";
  }
};

export const getLabelByParentType = ({ t, parentType }) => {
  switch (parentType) {
    case "ASSESSMENT_CATEGORIES":
      return t("academicSetup:category_set");
    case "GRADE_BOUNDARIES":
    case "GRADE_SCALES":
      return t("academicSetup:grade_boundary");
    default:
      return "";
  }
};

export const getUpdatedGradeOptions = _.memoize(
  ({
    t,
    gradeOptions,
    termsCategorySetsMappingWithGradesData,
    sourceTermLabel,
    isImportFromAnotherTerm,
    parentType,
    canOverrideExistingTemplates,
    gradeId,
    subjectId,
    sourceSubjectId,
  }) => {
    if (canOverrideExistingTemplates) {
      if (isImportFromAnotherTerm) return gradeOptions;
      return _.map(gradeOptions, option => {
        let isDisabled = false;
        const currGradeId = _.get(option, "value");
        if (!isImportFromAnotherTerm && subjectId === sourceSubjectId) {
          isDisabled = gradeId === currGradeId;
        }

        return {
          ...option,
          disabled: isDisabled,
        };
      });
    }

    return _.map(gradeOptions, option => {
      const gradeId = _.get(option, "value");
      const isSourceTermCategorySetMappedWithGrade = _.get(
        termsCategorySetsMappingWithGradesData,
        `0.categorySetsMappingWithGrades.${gradeId}`
      );

      const isTargetTermCategorySetMappedWithGrade = _.get(
        termsCategorySetsMappingWithGradesData,
        `1.categorySetsMappingWithGrades.${gradeId}`
      );

      const isDisabled =
        (isImportFromAnotherTerm && !isSourceTermCategorySetMappedWithGrade) ||
        isTargetTermCategorySetMappedWithGrade;

      const itemTypeLabel = getLabelByParentType({ t, parentType });

      let disabledTooltip = t(
        "academicSetup:label_already_exists_for_this_grade",
        { label: itemTypeLabel }
      );

      const itemTypeLabelPlural = getPluralLabelByParentType({ t, parentType });

      if (isImportFromAnotherTerm && !isSourceTermCategorySetMappedWithGrade) {
        disabledTooltip = t(
          "academicSetup:no_label1_created_for_this_subject_and_grade_in_label2",
          { label1: itemTypeLabelPlural, label2: sourceTermLabel }
        );
      }

      return {
        ...option,
        disabled: isDisabled,
        disabledTooltip,
      };
    });
  },
  params => JSON.stringify(params)
);

export const getSelectedGradesDropdownLabel = _.memoize(
  ({ t, selectedGradesOptions, grades, isSubjectSelected }) => {
    if (!isSubjectSelected) return t("common:select");
    if (_.isEmpty(selectedGradesOptions))
      return t("common:please_select_a_grade");
    const selectedGrades = _.map(selectedGradesOptions, gradeId =>
      _.get(
        _.find(grades, grade => gradeId === _.get(grade, "id")),
        "name",
        ""
      )
    );
    const selectedGradesCount = _.size(selectedGrades);
    if (selectedGradesCount === _.size(grades)) return t("common:all_grades");
    let selectedGradesDropdownLabel = _.join(
      _.slice(selectedGrades, 0, 2),
      ", "
    );
    if (selectedGradesCount > 2) {
      selectedGradesDropdownLabel = `${selectedGradesDropdownLabel}, +${t(
        "common:number_more",
        { number: selectedGradesCount - 2 }
      )}`;
    }
    return selectedGradesDropdownLabel;
  },
  params => JSON.stringify(params)
);

export const getSubjectCheckboxDisabledTooltip = _.memoize(
  ({
    t,
    termsCategorySetsMappingWithGradesData,
    sourceTermLabel,
    isImportFromAnotherTerm,
    parentType,
    sourceSubjectGenericTags,
    subjectLevelGenericTags,
    isCurriculumDP,
  }) => {
    const tooltip = t("academicSetup:label_already_exists_for_this_subject", {
      label: getPluralLabelByParentType({ t, parentType }),
    });
    const isNoCommonGenericTags = _.isEmpty(
      _.intersection(
        _.map(sourceSubjectGenericTags, "id"),
        _.map(subjectLevelGenericTags, "id")
      )
    );
    const doBothSourceAndTargetSubjectsHaveSLorHL =
      !_.isEmpty(sourceSubjectGenericTags) &&
      !_.isEmpty(subjectLevelGenericTags);
    if (
      !isImportFromAnotherTerm &&
      isCurriculumDP &&
      isNoCommonGenericTags &&
      doBothSourceAndTargetSubjectsHaveSLorHL
    ) {
      const localeKey =
        "academicSetup:template_cannot_be_duplicated_to_subjects_with_label";
      const templateLabel = getLabelByParentType({ t, parentType });
      const genericTagLabel = _.get(_.first(subjectLevelGenericTags), "label");
      return t(localeKey, { template: templateLabel, label: genericTagLabel });
    }

    if (
      _.get(
        termsCategorySetsMappingWithGradesData,
        "1.isEveryGradeHasCategorySet"
      )
    ) {
      return tooltip;
    } else if (
      isImportFromAnotherTerm &&
      _.get(termsCategorySetsMappingWithGradesData, "0.isCategorySetsEmpty")
    ) {
      return t(
        "academicSetup:no_label_created_for_this_subject_in_term_label",
        {
          label: getPluralLabelByParentType({ t, parentType }),
          termLabel: sourceTermLabel,
        }
      );
    }
    return tooltip;
  },
  params => JSON.stringify(params)
);

export const getRootNodesForBulkImport = _.memoize(
  ({ allRootNodes, isImportFromAnotherTerm }) => {
    return isImportFromAnotherTerm
      ? allRootNodes
      : _.filter(
          allRootNodes,
          node => !_.includes(CORE_TOK_AND_OTHER_TYPES, _.get(node, "type"))
        );
  },
  params => JSON.stringify(params)
);

export const getSubjectGroupsForBulkImport = _.memoize(
  ({ allSubjectGroups, rootNodes, isImportFromAnotherTerm }) => {
    const subjectGroupsData = _.map(allSubjectGroups, subjectGroup => {
      const { subjects } = subjectGroup;
      return {
        ...subjectGroup,
        subjects: _.map(subjects, subject => {
          const subjectNode = _.find(
            rootNodes,
            node => node?.id === subject?.id
          );
          return {
            ...subject,
            pointAssessmentSets: _.get(subjectNode, "pointAssessmentSets", []),
          };
        }),
      };
    });

    let excludedSubjectGroupTypes = [];
    if (!isImportFromAnotherTerm) {
      excludedSubjectGroupTypes = CORE_AND_OTHER_TYPES;
    }

    return _.isEmpty(excludedSubjectGroupTypes)
      ? subjectGroupsData
      : _.filter(
          subjectGroupsData,
          subjectGroup =>
            !_.includes(excludedSubjectGroupTypes, _.get(subjectGroup, "type"))
        );
  },
  params => JSON.stringify(params)
);

export const getGradesOptionsForBulkImport = _.memoize(
  ({
    grades,
    subjectGroups,
    rootNodes,
    isCurriculumDP,
    targetTermId,
    canOverrideExistingTemplates,
  }) => {
    return _.map(grades, grade => {
      const {
        isCategorySetsExistsForAllSubjects,
      } = getCategorySetsMappedWithAllSubjectsData({
        subjectGroups,
        rootNodes,
        gradeId: _.get(grade, "id"),
        termId: targetTermId,
        isCurriculumDP,
      });

      return {
        label: _.get(grade, "name"),
        value: _.get(grade, "id"),
        disabled:
          !canOverrideExistingTemplates && isCategorySetsExistsForAllSubjects,
      };
    });
  },
  params => JSON.stringify(params)
);

export const getTermsOptionsForBulkImport = _.memoize(
  ({
    t,
    terms,
    subjectGroups,
    rootNodes,
    grades,
    isCurriculumDP,
    targetTermId,
  }) => {
    return _.map(
      _.filter(terms, term => _.get(term, "id") !== targetTermId),
      term => {
        const { categorySetsCount } = getCategorySetsMappedWithAllSubjectsData({
          subjectGroups,
          rootNodes,
          grades,
          termId: _.get(term, "id"),
          isCurriculumDP,
        });

        return {
          label: _.get(term, "label"),
          value: _.get(term, "id"),
          disabled: categorySetsCount === 0,
          disabledTooltip: t(
            "academicSetup:no_category_sets_created_in_this_term"
          ),
        };
      }
    );
  },
  params => JSON.stringify(params)
);

export const getBulkDuplicateTemplateHeaderSubtext = ({
  t,
  isImportFromAnotherTerm,
  sourceTermLabel = "",
  targetTermLabel = "",
  parentType = "ASSESSMENT_CATEGORIES",
}) => {
  if (isImportFromAnotherTerm) {
    const label = getPluralLabelByParentType({ t, parentType });

    return (
      <Trans
        i18nKey="academicSetup:bulk_import_subtext_with_labels"
        components={components}
        values={{
          label,
          label1: sourceTermLabel,
          label2: targetTermLabel,
        }}
      >
        {{ label }} created in <b>’{{ label1: sourceTermLabel }}’</b> would be
        imported to ‘{{ label2: targetTermLabel }}’ for the subjects selected
        below
      </Trans>
    );
  }

  if (parentType === "GRADE_SCALES") {
    return t(
      "academicSetup:this_grade_boundary_will_be_copied_to_all_the_subjects_and_grade_levels_selected_below"
    );
  }

  const label = getLabelByParentType({ t, parentType });
  return t("academicSetup:bulk_duplicate_subtext_with_label", { label });
};

export const getBulkDuplicateTemplateHeaderTitle = _.memoize(
  ({
    parentType,
    isImportFromAnotherTerm,
    targetTermLabel,
    templateName,
    t,
    subjectName,
    gradeName,
    currentStepIndex,
  }) => {
    if (currentStepIndex === 1) return t("common:summary_and_confirmation");
    if (isImportFromAnotherTerm) {
      return t("academicSetup:import_label_from_another_term_to_term_label", {
        label: getPluralLabelByParentType({ t, parentType }),
        termLabel: targetTermLabel,
      });
    } else {
      if (parentType === "GRADE_SCALES") {
        return t(
          "academicSetup:duplicate_label_grade_boundary_to_other_subjects_and_grade_levels",
          { label: `${targetTermLabel} - ${subjectName} - ${gradeName}` }
        );
      }
      const label =
        parentType === "ASSESSMENT_CATEGORIES"
          ? templateName
          : `${templateName} - ${subjectName}`;
      return t("academicSetup:select_subjects_to_duplicate_label_to", {
        label,
      });
    }
  },
  params => JSON.stringify(params)
);

export const getAbsolutePercentageValueMemoize = _.memoize(
  params => getAbsolutePercentageValue(params),
  params => JSON.stringify(params)
);

const getAbsolutePercentageValue = ({ key, fieldValues, value }) => {
  const totalWeight = _.reduce(
    fieldValues,
    (totalWeight, fieldValue) => {
      return totalWeight + _.toNumber(_.get(fieldValue, key, 0));
    },
    0
  );

  return totalWeight > 0 ? `${_.round((value / totalWeight) * 100, 2)}%` : "";
};

export const getTemplateLabelFromId = _.memoize(
  ({ id, organizationPointSet }) => {
    return _.get(
      _.find(
        organizationPointSet,
        templateNode => _.get(templateNode, "node.id") === id
      ),
      "node.label",
      ""
    );
  },
  params => JSON.stringify(params)
);

export const getAssessmentCategoriesDeleteModalLocaleKeys = _.memoize(
  ({ itemType }) => {
    const localeKeyForItem =
      itemType === "CATEGORY_SET" ? "common:category_set" : "common:category";
    const localeKeyForDeleteConfirmation =
      itemType === "CATEGORY_SET"
        ? "academicSetup:delete_unmapped_category_set_confirmation_with_label"
        : "academicSetup:delete_unmapped_category_confirmation_with_label";
    return { localeKeyForItem, localeKeyForDeleteConfirmation };
  },
  params => JSON.stringify(params)
);

export const getGradeScaleTypeOptions = ({ t }) => {
  return [
    {
      key: "TERM_GRADE_SCALE",
      label: t("academicSetup:use_predefined_grade_boundary"),
      subText: t("academicSetup:use_predefined_grade_boundary_subtext"),
    },
    {
      key: "CUSTOM",
      label: t("academicSetup:define_a_custom_grade_boundary"),
      subText: t("academicSetup:define_a_custom_grade_boundary_subtext"),
    },
  ];
};

export const getCategoryGradeScaleSet = ({
  gradeScaleSet,
  gradeScaleType,
  gradeBoundaryId,
  defaultGradeScaleSet,
  overallCategoryGradeBoundaryValues,
}) => {
  let categoryGradeScaleSet;
  if (
    gradeScaleType === "CUSTOM" &&
    gradeBoundaryId &&
    _.isEmpty(gradeScaleSet)
  ) {
    categoryGradeScaleSet = _.get(
      overallCategoryGradeBoundaryValues,
      gradeBoundaryId
    );
  } else if (
    gradeScaleType === "TERM_GRADE_SCALE" &&
    _.isEmpty(gradeScaleSet)
  ) {
    categoryGradeScaleSet = defaultGradeScaleSet;
  } else {
    categoryGradeScaleSet = gradeScaleSet;
  }

  return categoryGradeScaleSet;
};

export const getOverriddenGradeBoundaries = _.memoize(
  ({ grades, rootNodes, associatedEntities }) => {
    const overriddenGradeBoundaries = [];

    _.forEach(associatedEntities, associatedEntity => {
      const { subjectId, gradeIds } = associatedEntity;

      const subject = _.find(
        rootNodes,
        rootNode => _.get(rootNode, "id") === subjectId
      );

      const subjectName = _.get(subject, "name", "");

      _.forEach(gradeIds, gradeId => {
        const gradeName = _.get(
          _.find(grades, grade => _.get(grade, "id") === gradeId),
          "name",
          ""
        );
        overriddenGradeBoundaries.push({
          key: `${subjectId}-${gradeId}`,
          subjectName,
          gradeName,
        });
      });
    });

    return overriddenGradeBoundaries;
  },
  params => JSON.stringify(params)
);

export const UPDATE_TEMPLATE_FIELD_VALUE = "UPDATE_TEMPLATE_FIELDS_VALUE";
export const UPDATE_TEMPLATE_FIELDS_SET = "UPDATE_TEMPLATE_FIELDS_SET";
export const INIT_TEMPLATE_FIELDS_SET = "INIT_TEMPLATE_FIELDS_SET";
export const SET_IS_EDIT_TEMPLATE_MODE = "SET_IS_EDIT_TEMPLATE_MODE";
export const SET_USED_COURSES = "SET_USED_COURSES";
export const SET_USED_CATEGORY_SET_NAMES = "SET_USED_CATEGORY_SET_NAMES";
export const UPDATE_CURRENT_CATEGORY_GRADE_BOUNDARY_VALUES =
  "UPDATE_CURRENT_CATEGORY_GRADE_BOUNDARY_VALUES";
export const UPDATE_OVERALL_CATEGORY_GRADE_BOUNDARY_VALUES =
  "UPDATE_OVERALL_CATEGORY_GRADE_BOUNDARY_VALUES";

const REDUCER_HANDLERS = {
  [UPDATE_TEMPLATE_FIELD_VALUE]: (state, action) => {
    const params = action.data;
    const type = params.type;
    const data = params.data;
    const arrayindex = params.index;

    if (type == "add") {
      state = update(state, {
        templateFieldsObj: { fieldValues: { $push: [data] } },
      });
    } else if (type == "delete") {
      state = update(state, {
        templateFieldsObj: { fieldValues: { $splice: [[arrayindex, 1]] } },
      });
    } else {
      state = update(state, {
        templateFieldsObj: {
          fieldValues: { $splice: [[arrayindex, 1, data]] },
        },
      });
    }
    return state;
  },
  [UPDATE_TEMPLATE_FIELDS_SET]: (state, action) => {
    const params = action.data;
    Object.keys(params).map(key => {
      state = update(state, {
        templateFieldsObj: { [key]: { $set: params[key] } },
      });
    });
    return state;
  },
  [INIT_TEMPLATE_FIELDS_SET]: state => {
    return update(state, {
      templateFieldsObj: { $set: _.cloneDeep(initialState.templateFieldsObj) },
    });
  },
  [SET_IS_EDIT_TEMPLATE_MODE]: (state, action) => {
    return update(state, {
      isEditMode: { $set: action.data },
    });
  },
  [SET_USED_COURSES]: (state, action) => {
    return update(state, {
      usedCourses: { $set: action.data },
    });
  },
  [SET_USED_CATEGORY_SET_NAMES]: (state, action) => {
    return update(state, {
      usedCategorySetNames: { $set: action.data },
    });
  },
  [UPDATE_CURRENT_CATEGORY_GRADE_BOUNDARY_VALUES]: (state, action) => {
    return update(state, {
      currentCategoryGradeBoundaryValues: { $set: action.data },
    });
  },
  [UPDATE_OVERALL_CATEGORY_GRADE_BOUNDARY_VALUES]: (state, action) => {
    let updatedData = {};
    if (!_.isEmpty(action.data)) {
      const existingGradeBoundaryValues = _.get(
        state,
        "overallCategoryGradeBoundaryValues",
        []
      );
      const gradeBoundaryId = _.get(action.data, "gradeBoundaryId");
      const newCategoryGradeBoundaryValues = _.get(
        action.data,
        "categoryGradeBoundaryValues",
        []
      );
      updatedData = {
        ...existingGradeBoundaryValues,
        [gradeBoundaryId]: newCategoryGradeBoundaryValues,
      };
    }

    return update(state, {
      overallCategoryGradeBoundaryValues: {
        $set: updatedData,
      },
    });
  },
};

export const initialState = {
  templateFieldsObj: {
    id: null,
    label: "",
    isShowGradeBoundaries: false,
    courses: [],
    fieldValues: [
      {
        id: null,
        label: "",
        color: "#008392",
        percentage_weight: "",
        slWeight: "",
        hlWeight: "",
        gradeBoundaryId: null,
        displaySequence: 1,
        gradeScaleType: null,
      },
    ],
  },
  currentCategoryGradeBoundaryValues: [],
  overallCategoryGradeBoundaryValues: {},
  isEditMode: false,
  usedCourses: [],
  usedCategorySetNames: [],
};

export const scoreCategoriesReducer = (state = initialState, action) => {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
};

export const getScoreCategoriesInitialState = () => {
  return _.cloneDeep(initialState);
};

export const getScoreCategoriesStateWithUpdatedTemplateFieldsForEditing = ({
  set,
}) => {
  const initialState = getScoreCategoriesInitialState();

  return {
    ...initialState,
    templateFieldsObj: {
      ...set,
    },
    isEditMode: true,
  };
};

export const getGradeConfigurationEntityGroupFilters = ({
  isCurriculumMYP,
  grades,
  configurationGroups,
}) => {
  if (!isCurriculumMYP) return [];

  return _.map(grades, ({ id: gradeId }) => ({
    configurationEntityGroups: [
      {
        entityId: gradeId,
        entityType: "GRADE",
      },
    ],
    configurations: configurationGroups,
  }));
};
